import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, RefreshCw, AlertTriangle, X, Tag } from 'lucide-react';
import apiService from '../../services/api';
import PageHeader from '../common/PageHeader';

const CategoriesTab = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [sortBy, setSortBy] = useState('spending'); // spending, alphabetical, usage, recent

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getCategoryStats();
      setCategories(response);
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (categoryData) => {
    try {
      const newCategory = await apiService.createCategory(categoryData);
      setCategories(prevCategories => [...prevCategories, newCategory]);
      setIsAddModalOpen(false);
    } catch (err) {
      console.error('Error creating category:', err);
      throw err;
    }
  };

  const handleUpdateCategory = async (categoryId, categoryData) => {
    try {
      const updatedCategory = await apiService.updateCategory(categoryId, categoryData);
      setCategories(prevCategories =>
        prevCategories.map(cat =>
          cat.id === categoryId ? updatedCategory : cat
        )
      );
      setEditingCategory(null);
    } catch (err) {
      console.error('Error updating category:', err);
      throw err;
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await apiService.deleteCategory(categoryId);
      setCategories(prevCategories =>
        prevCategories.filter(cat => cat.id !== categoryId)
      );
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category');
    }
  };

  const handleCategoryClick = (category) => {
    // Navigate to Transactions tab filtered by this category
    navigate(`/?tab=transactions&category=${encodeURIComponent(category.name)}`);
  };

  const getSortedCategories = () => {
    const sorted = [...categories];
    switch (sortBy) {
      case 'spending':
        return sorted.sort((a, b) => b.spending_last_30_days - a.spending_last_30_days);
      case 'alphabetical':
        return sorted.sort((a, b) => a.label.localeCompare(b.label));
      case 'usage':
        return sorted.sort((a, b) => b.transactions_last_30_days - a.transactions_last_30_days);
      case 'recent':
        return sorted.sort((a, b) => {
          if (!a.last_used) return 1;
          if (!b.last_used) return -1;
          return new Date(b.last_used) - new Date(a.last_used);
        });
      default:
        return sorted;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      red: 'bg-red-100 text-red-800 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      pink: 'bg-pink-100 text-pink-800 border-pink-200',
      indigo: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
      orange: 'bg-orange-100 text-orange-800 border-orange-200',
      teal: 'bg-teal-100 text-teal-800 border-teal-200'
    };
    return colorMap[color] || colorMap.blue;
  };

  const getColorCircle = (color) => {
    const colorMap = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      red: 'bg-red-500',
      yellow: 'bg-yellow-500',
      purple: 'bg-purple-500',
      pink: 'bg-pink-500',
      indigo: 'bg-indigo-500',
      gray: 'bg-gray-500',
      orange: 'bg-orange-500',
      teal: 'bg-teal-500'
    };
    return colorMap[color] || colorMap.blue;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading categories...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <div className="mt-4">
                <button
                  onClick={loadCategories}
                  className="text-sm font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        icon={Tag}
        title="Categories"
        subtitle="Manage transaction categories and view usage statistics"
      >
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          <option value="spending">Spending</option>
          <option value="usage">Usage</option>
          <option value="recent">Recent</option>
          <option value="alphabetical">A-Z</option>
        </select>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Add Category</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-3 sm:px-4 lg:px-8">

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {getSortedCategories().map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-all group"
          >
            {/* Clickable category content */}
            <div
              onClick={() => handleCategoryClick(category)}
              className="p-4 cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center flex-1">
                  <span className="text-2xl mr-3">{category.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {category.label}
                    </h3>
                    <p className="text-xs text-gray-500">{category.name}</p>
                  </div>
                </div>
              </div>

              {/* Stats Section */}
              <div className="space-y-2 mb-3">
                {/* Spending in last 30 days */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">30-day spending:</span>
                  <span className="font-semibold text-gray-900">
                    {category.spending_last_30_days > 0 ? formatCurrency(category.spending_last_30_days) : '$0'}
                  </span>
                </div>

                {/* Transaction count */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Transactions:</span>
                  <span className="font-medium text-gray-700">
                    {category.transactions_last_30_days} / {category.total_transactions}
                  </span>
                </div>
              </div>

              {/* Color and badges */}
              <div className="flex items-center space-x-2">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full ${getColorCircle(category.color)} mr-1.5`}></div>
                  <span className="text-xs text-gray-600 capitalize">{category.color}</span>
                </div>
                {category.is_system && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                    System
                  </span>
                )}
                {category.total_transactions === 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                    Unused
                  </span>
                )}
              </div>
            </div>

            {/* Action buttons - outside clickable area */}
            <div className="border-t border-gray-100 px-4 py-2 bg-gray-50 rounded-b-lg flex items-center justify-end space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCategory(category);
                }}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Edit category"
              >
                <Edit2 className="h-4 w-4" />
              </button>
              {!category.is_system && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteConfirm(category);
                  }}
                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Delete category"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Category Modal */}
      {isAddModalOpen && (
        <CategoryModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleAddCategory}
          title="Add Category"
        />
      )}

      {/* Edit Category Modal */}
      {editingCategory && (
        <CategoryModal
          isOpen={!!editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={(data) => handleUpdateCategory(editingCategory.id, data)}
          category={editingCategory}
          title="Edit Category"
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">Delete Category</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete "{deleteConfirm.label}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCategory(deleteConfirm.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Category Modal Component
const CategoryModal = ({ isOpen, onClose, onSave, category, title }) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    label: category?.label || '',
    icon: category?.icon || '📄',
    color: category?.color || 'blue',
    sort_order: category?.sort_order || 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [suggestedIcons, setSuggestedIcons] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  const colors = [
    'blue', 'green', 'red', 'yellow', 'purple',
    'pink', 'indigo', 'gray', 'orange', 'teal'
  ];

  // AI-powered icon search function
  const searchIcons = async (query) => {
    if (!query.trim()) {
      setSuggestedIcons([]);
      return;
    }

    setIsSearching(true);
    try {
      // Create a comprehensive icon-to-keyword mapping for AI-like suggestions
      const iconMappings = {
        // Food & Dining
        '🍔': ['burger', 'hamburger', 'fast food', 'mcdonalds', 'food', 'eating', 'lunch', 'dinner'],
        '🍕': ['pizza', 'italian', 'food', 'slice', 'eating', 'dinner', 'restaurant'],
        '🌮': ['taco', 'mexican', 'food', 'spicy', 'lunch', 'dinner'],
        '🍝': ['pasta', 'spaghetti', 'italian', 'food', 'noodles', 'dinner'],
        '🍜': ['noodles', 'soup', 'ramen', 'asian', 'food', 'hot', 'lunch'],
        '☕': ['coffee', 'cafe', 'drink', 'morning', 'caffeine', 'hot', 'starbucks'],
        '🍺': ['beer', 'alcohol', 'drink', 'bar', 'party', 'social', 'brewery'],
        '🍷': ['wine', 'alcohol', 'drink', 'dinner', 'fancy', 'restaurant', 'red wine'],
        '🥛': ['milk', 'drink', 'dairy', 'breakfast', 'healthy', 'white'],
        '🧃': ['juice', 'drink', 'fruit', 'healthy', 'kids', 'breakfast'],

        // Transportation
        '🚗': ['car', 'drive', 'vehicle', 'auto', 'transport', 'road', 'commute', 'uber', 'lyft'],
        '🚕': ['taxi', 'cab', 'yellow', 'ride', 'transport', 'city', 'uber'],
        '🚌': ['bus', 'public transport', 'commute', 'city', 'transit'],
        '🚂': ['train', 'railway', 'commute', 'transport', 'travel'],
        '✈️': ['plane', 'airplane', 'flight', 'travel', 'vacation', 'airport', 'fly'],
        '🚲': ['bike', 'bicycle', 'cycle', 'exercise', 'green', 'transport'],
        '🏍️': ['motorcycle', 'bike', 'motor', 'fast', 'ride'],
        '⛽': ['gas', 'fuel', 'petrol', 'station', 'car', 'fill up'],
        '🚁': ['helicopter', 'chopper', 'fly', 'emergency', 'air'],

        // Shopping & Retail
        '🛒': ['shopping', 'cart', 'grocery', 'store', 'buy', 'retail', 'supermarket'],
        '🛍️': ['shopping', 'bags', 'retail', 'buy', 'store', 'mall'],
        '💳': ['credit card', 'payment', 'money', 'bank', 'finance', 'pay', 'card'],
        '💰': ['money', 'cash', 'wealth', 'finance', 'rich', 'salary', 'income'],
        '💸': ['money', 'cash', 'spending', 'expense', 'pay', 'flying money'],
        '🏪': ['store', 'shop', 'retail', 'business', 'convenience'],
        '🏬': ['mall', 'shopping center', 'retail', 'department store'],
        '💎': ['diamond', 'jewelry', 'expensive', 'luxury', 'valuable'],
        '👕': ['shirt', 'clothing', 'clothes', 'fashion', 'wear'],
        '👖': ['pants', 'jeans', 'clothing', 'clothes', 'fashion'],

        // Health & Medical
        '🏥': ['hospital', 'medical', 'health', 'doctor', 'emergency', 'clinic'],
        '💊': ['pill', 'medicine', 'pharmacy', 'drug', 'medication', 'health'],
        '💉': ['injection', 'vaccine', 'medical', 'doctor', 'shot', 'needle'],
        '🩺': ['stethoscope', 'doctor', 'medical', 'health', 'checkup'],
        '🦷': ['tooth', 'dental', 'dentist', 'teeth', 'oral health'],
        '🧬': ['dna', 'genetics', 'science', 'medical', 'biology'],
        '🔬': ['microscope', 'science', 'lab', 'research', 'medical'],
        '🌡️': ['thermometer', 'temperature', 'fever', 'sick', 'health'],

        // Home & Utilities
        '🏠': ['home', 'house', 'residence', 'property', 'real estate'],
        '💡': ['light', 'electricity', 'utilities', 'power', 'bright', 'idea'],
        '🔌': ['plug', 'electricity', 'power', 'utilities', 'outlet'],
        '🔋': ['battery', 'power', 'energy', 'charge', 'electric'],
        '📱': ['phone', 'mobile', 'smartphone', 'cell', 'iphone', 'android'],
        '💻': ['laptop', 'computer', 'work', 'tech', 'office'],
        '📺': ['tv', 'television', 'entertainment', 'watch', 'screen'],
        '⚡': ['lightning', 'electricity', 'power', 'energy', 'fast', 'danger'],
        '💧': ['water', 'utilities', 'drop', 'liquid', 'blue'],

        // Entertainment & Hobbies
        '🎬': ['movie', 'cinema', 'film', 'entertainment', 'theater'],
        '🎮': ['gaming', 'video games', 'play', 'console', 'xbox', 'playstation'],
        '🎨': ['art', 'paint', 'creative', 'design', 'hobby', 'artist'],
        '🎯': ['target', 'goal', 'aim', 'dartboard', 'precision'],
        '🎲': ['dice', 'game', 'gambling', 'chance', 'board game'],
        '🎸': ['guitar', 'music', 'instrument', 'rock', 'band'],
        '🎤': ['microphone', 'singing', 'music', 'performance', 'karaoke'],
        '📚': ['books', 'reading', 'study', 'library', 'education'],
        '🎭': ['theater', 'drama', 'acting', 'performance', 'masks'],

        // Work & Education
        '💼': ['briefcase', 'work', 'business', 'office', 'professional'],
        '📊': ['chart', 'graph', 'data', 'analytics', 'business', 'statistics'],
        '📈': ['trending up', 'growth', 'profit', 'success', 'increase'],
        '📉': ['trending down', 'loss', 'decrease', 'decline', 'chart'],
        '🎓': ['graduation', 'education', 'school', 'university', 'degree'],
        '📝': ['writing', 'notes', 'document', 'memo', 'text'],
        '🖊️': ['pen', 'writing', 'signature', 'ink'],
        '📋': ['clipboard', 'list', 'checklist', 'document'],

        // Finance & Banking
        '🏦': ['bank', 'banking', 'finance', 'money', 'institution'],
        '💹': ['stock market', 'trading', 'finance', 'investment', 'chart'],
        '💱': ['currency', 'exchange', 'money', 'foreign', 'forex'],
        '💲': ['dollar', 'money', 'currency', 'price', 'cost'],
        '🔒': ['lock', 'security', 'safe', 'protection', 'secure'],
        '🗝️': ['key', 'unlock', 'access', 'security', 'password'],

        // Nature & Weather
        '🌱': ['plant', 'growth', 'green', 'nature', 'seedling'],
        '🌳': ['tree', 'nature', 'environment', 'green', 'forest'],
        '🌹': ['rose', 'flower', 'love', 'romantic', 'red'],
        '☀️': ['sun', 'sunny', 'weather', 'bright', 'hot'],
        '🌧️': ['rain', 'weather', 'wet', 'storm'],
        '❄️': ['snow', 'cold', 'winter', 'ice', 'freeze'],

        // Animals & Pets
        '🐶': ['dog', 'puppy', 'pet', 'animal', 'cute'],
        '🐱': ['cat', 'kitten', 'pet', 'animal', 'cute'],
        '🐭': ['mouse', 'small', 'animal', 'rodent'],
        '🐻': ['bear', 'animal', 'wild', 'cute'],
        '🦁': ['lion', 'king', 'wild', 'animal', 'strong'],

        // Substances & Vices
        '🚬': ['cigarette', 'smoking', 'tobacco', 'smoke', 'nicotine', 'addiction'],
        '🍃': ['leaf', 'weed', 'marijuana', 'cannabis', 'green', 'plant'],

        // Hazard & Warning
        '⚠️': ['warning', 'hazard', 'caution', 'alert', 'danger', 'attention'],
        '☢️': ['radioactive', 'nuclear', 'hazard', 'danger', 'toxic'],
        '☣️': ['biohazard', 'biological', 'hazard', 'danger', 'toxic'],
        '🚫': ['prohibited', 'no', 'ban', 'forbidden', 'stop', 'not allowed'],
        '⛔': ['no entry', 'stop', 'prohibited', 'forbidden', 'blocked'],
        '🔥': ['fire', 'hot', 'trending', 'popular', 'burn', 'flame'],
        '💥': ['explosion', 'blast', 'boom', 'dangerous', 'impact'],
        '🔴': ['red', 'stop', 'danger', 'alert', 'warning'],
        '🔺': ['triangle', 'warning', 'caution', 'alert', 'up'],

        // Common symbols
        '⭐': ['star', 'favorite', 'rating', 'quality', 'excellent'],
        '❤️': ['heart', 'love', 'like', 'favorite', 'red'],
        '💯': ['hundred', 'perfect', 'score', 'complete', 'all'],
        '✨': ['sparkles', 'magic', 'special', 'shiny', 'stars'],
        '💫': ['dizzy', 'stars', 'magic', 'special', 'sparkle'],
        '📄': ['document', 'paper', 'file', 'text', 'page', 'other']
      };

      // Search for matching icons based on query
      const queryLower = query.toLowerCase();
      const matches = [];

      Object.entries(iconMappings).forEach(([icon, keywords]) => {
        const score = keywords.reduce((acc, keyword) => {
          if (keyword.includes(queryLower) || queryLower.includes(keyword)) {
            return acc + (keyword === queryLower ? 10 : keyword.startsWith(queryLower) ? 5 : 1);
          }
          return acc;
        }, 0);

        if (score > 0) {
          matches.push({ icon, score, keywords: keywords.slice(0, 3) });
        }
      });

      // Sort by relevance score and take top 12 results
      const sortedMatches = matches
        .sort((a, b) => b.score - a.score)
        .slice(0, 12)
        .map(match => match.icon);

      setSuggestedIcons(sortedMatches);
    } catch (err) {
      console.error('Error searching icons:', err);
      setSuggestedIcons([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchIcons(iconSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [iconSearchQuery]);

  const iconCategories = {
    'Food & Dining': [
      '🍔', '🍕', '🌮', '🍝', '🍜', '🍱', '🍙', '🍘', '🥘', '🍲',
      '🥗', '🥙', '🌯', '🥪', '🍖', '🍗', '🥓', '🍤', '🍣', '🦞',
      '🧆', '🥟', '🍩', '🧁', '🍰', '🎂', '🍪', '🍫', '🍬', '🍭',
      '🍯', '🥛', '☕', '🍵', '🧃', '🥤', '🍺', '🍷', '🥂', '🍾',
      '🍴', '🥄', '🔪', '🍽️', '🥢'
    ],
    'Transportation': [
      '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐',
      '🛻', '🚚', '🚛', '🚜', '🏍️', '🛵', '🚲', '🛴', '🚁', '✈️',
      '🛩️', '🚀', '🛸', '🚂', '🚝', '🚄', '🚅', '🚆', '🚇', '🚈',
      '🚉', '🚊', '🚋', '🚞', '🚟', '🚠', '🚡', '⛵', '🛥️', '🚤',
      '⛴️', '🛳️', '🚢', '⚓', '⛽', '🚧', '🚥', '🚦', '🛣️', '🗺️'
    ],
    'Shopping & Retail': [
      '🛒', '🛍️', '🏪', '🏬', '🏢', '🏭', '🏤', '💳', '💰', '💸',
      '💵', '💴', '💶', '💷', '🪙', '💎', '⚖️', '🧾', '📦', '📋',
      '🛏️', '🪑', '🚪', '🪟', '🧸', '🎁', '🎀', '🛡️', '⚔️', '🔫',
      '🏹', '🎣', '🥽', '🥼', '👔', '👕', '👖', '🧥', '👗', '👠',
      '👢', '👟', '🧦', '🧤', '🎩', '👒', '🧢', '👑', '💍', '👜'
    ],
    'Health & Medical': [
      '🏥', '💊', '💉', '🩹', '🩺', '🦷', '🧬', '🔬', '🧪', '🧫',
      '🩻', '🫀', '🫁', '🧠', '👁️', '🦴', '🩸', '💉', '🌡️', '🔥',
      '❄️', '🧊', '💧', '🚿', '🛁', '🧴', '🧼', '🪥', '🧽', '🧹',
      '🪣', '🧷', '📍', '✂️', '📎', '🖇️', '📏', '📐', '📌', '🔖'
    ],
    'Home & Utilities': [
      '🏠', '🏡', '🏘️', '🏚️', '🏗️', '🏢', '🏬', '🏭', '🏪', '🏫',
      '💡', '🔌', '🔋', '🪫', '💻', '🖥️', '🖨️', '⌨️', '🖱️', '📱',
      '☎️', '📞', '📠', '📺', '📻', '🎙️', '🎚️', '🎛️', '⏰', '🕰️',
      '⏲️', '⏱️', '🧭', '📡', '🛰️', '🔭', '🔬', '🕳️', '💣', '🧨',
      '🔥', '💧', '🌊', '❄️', '⛄', '🌨️', '☃️', '🌬️', '💨', '☁️'
    ],
    'Entertainment & Hobbies': [
      '🎬', '🎭', '🎪', '🎨', '🎯', '🎲', '🎮', '🕹️', '🎰', '🎳',
      '🎸', '🎹', '🥁', '🎷', '🎺', '🎻', '🪕', '🎤', '🎧', '🎵',
      '🎶', '🎼', '🎹', '📚', '📖', '📝', '✏️', '🖊️', '🖋️', '🖌️',
      '🖍️', '📐', '📏', '🧮', '🔍', '🔎', '💿', '📀', '💽', '💾',
      '💻', '🖥️', '🖨️', '📷', '📸', '📹', '🎥', '📽️', '🎞️', '📺'
    ],
    'Education & Work': [
      '🎓', '📚', '📖', '📝', '📋', '📊', '📈', '📉', '📄', '📃',
      '📑', '📒', '📓', '📔', '📕', '📗', '📘', '📙', '📰', '🗞️',
      '📜', '🗒️', '🗓️', '📅', '📆', '🗃️', '🗂️', '🗄️', '📂', '📁',
      '💼', '👔', '🖇️', '📎', '📐', '📏', '📌', '📍', '✂️', '🖊️',
      '✏️', '🖋️', '🖌️', '🖍️', '📝', '💻', '🖥️', '⌨️', '🖱️', '🖨️'
    ],
    'Finance & Banking': [
      '💰', '💸', '💵', '💴', '💶', '💷', '🪙', '💳', '💎', '🏦',
      '🏧', '📊', '📈', '📉', '💹', '💱', '💲', '🧾', '📋', '📄',
      '⚖️', '🔒', '🔐', '🗝️', '🔑', '🛡️', '📦', '📮', '📪', '📫',
      '📬', '📭', '📯', '📊', '📈', '📉', '💹', '💱', '💲', '🔢',
      '🧮', '📐', '📏', '📌', '📍', '📎', '🖇️', '📋', '📄', '📃'
    ],
    'Travel & Vacation': [
      '✈️', '🏖️', '🏝️', '🗺️', '🧳', '🎒', '👜', '💼', '🌍', '🌎',
      '🌏', '🗾', '🧭', '⛰️', '🏔️', '🗻', '🌋', '🏕️', '🏞️', '🏜️',
      '🏖️', '⛱️', '🏊', '🏄', '🚣', '🛶', '⛵', '🚤', '🛥️', '🚢',
      '🎡', '🎢', '🎠', '🎪', '🎭', '🎨', '🗽', '🗼', '🏛️', '⛪',
      '🕌', '🕍', '⛩️', '🛕', '🏰', '🏯', '🏟️', '🎭', '🎪', '🎨'
    ],
    'Sports & Fitness': [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉', '🥏', '🎱',
      '🪀', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏', '🪃', '🥅', '⛳',
      '🪁', '🛝', '🛹', '🛼', '🎿', '⛷️', '🏂', '🪂', '🏋️', '🤸',
      '🤺', '🤾', '🏌️', '🏇', '🧘', '🏃', '🚶', '🧗', '🚴', '🏊',
      '🏄', '🚣', '🧜', '🦸', '🦹', '🧙', '🧚', '🧛', '🧟', '🧞'
    ],
    'Nature & Weather': [
      '🌱', '🌿', '🍀', '🌾', '🌳', '🌲', '🌴', '🌵', '🌶️', '🌽',
      '🥕', '🧄', '🧅', '🍄', '🌰', '🌹', '🌷', '🌸', '💐', '🌺',
      '🌻', '🌼', '🌙', '⭐', '🌟', '💫', '⚡', '☀️', '🌞', '🌝',
      '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓',
      '🌔', '☁️', '⛅', '⛈️', '🌤️', '🌦️', '🌧️', '⛆', '🌩️', '❄️'
    ],
    'Animals & Pets': [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯',
      '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🦍',
      '🦧', '🐺', '🐗', '🐴', '🦄', '🐝', '🪱', '🐛', '🦋', '🐌',
      '🐞', '🐜', '🪰', '🪲', '🪳', '🦟', '🦗', '🕷️', '🦂', '🐢',
      '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡'
    ],
    'Safety & Warning': [
      '⚠️', '☢️', '☣️', '🚫', '⛔', '🔴', '🔺', '💥', '⚡', '🔥',
      '☠️', '💀', '🛑', '🚷', '🚯', '🚱', '🚳', '🚭', '📵', '🔞',
      '❌', '❗', '❓', '‼️', '⁉️', '💢', '🌡️', '🧯', '🚨', '🆘'
    ],
    'Symbols & Misc': [
      '⭐', '🌟', '💫', '✨', '💤', '🕳️', '💬', '👁️‍🗨️', '🗨️', '🗯️',
      '💭', '💘', '💝', '💖', '💗', '💓', '💞', '💕', '💟', '❣️',
      '💔', '❤️‍🔥', '❤️‍🩹', '❤️', '🧡', '💛', '💚', '💙', '💜',
      '🤎', '🖤', '🤍', '💯', '💦', '💨', '👋', '✌️', '🤝', '👍',
      '👎', '👌', '🤏', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐'
    ]
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., food, transportation"
              required
              disabled={category?.is_system}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Label
            </label>
            <input
              type="text"
              value={formData.label}
              onChange={(e) => setFormData({ ...formData, label: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Food & Dining, Transportation"
              required
            />
          </div>

          {/* Icon Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Icon</label>
            <div>
              {/* Search bar */}
              <div className="mb-3">
                <input
                  type="text"
                  value={iconSearchQuery}
                  onChange={(e) => setIconSearchQuery(e.target.value)}
                  placeholder="Search for icons (e.g., food, car, home, smoking, money)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Currently selected icon */}
              {formData.icon && (
                <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
                  <span className="text-xl">{formData.icon}</span>
                  <span className="text-sm text-gray-600">Selected icon</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, icon: '' })}
                    className="ml-auto text-red-500 hover:text-red-700 text-sm"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Loading state */}
              {isSearching && (
                <div className="text-center py-4 text-gray-500">
                  Searching icons...
                </div>
              )}

              {/* Search results */}
              {iconSearchQuery && !isSearching && (
                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                  <div className="grid grid-cols-10 gap-1">
                    {suggestedIcons.map((icon, index) => (
                      <button
                        key={`search-${index}-${icon}`}
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, icon });
                          setIconSearchQuery('');
                        }}
                        className={`p-1 text-lg rounded hover:bg-gray-100 border-2 transition-colors ${
                          formData.icon === icon ? 'border-green-500 bg-green-50' : 'border-transparent'
                        }`}
                        title={icon}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                  {suggestedIcons.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      No icons found for "{iconSearchQuery}". Try keywords like: food, transport, money, health, shopping
                    </div>
                  )}
                </div>
              )}

              {/* Browse all icons when no search */}
              {!iconSearchQuery && (
                <div className="space-y-3">
                  <div className="text-sm text-gray-600 mb-2">
                    Browse categories or search above for specific icons
                  </div>
                  <div className="space-y-4 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                    {Object.entries(iconCategories).map(([categoryName, icons]) => (
                      <div key={categoryName}>
                        <h4 className="text-sm font-medium text-gray-600 mb-2 capitalize">
                          {categoryName} ({icons.length})
                        </h4>
                        <div className="grid grid-cols-10 gap-1">
                          {icons.map((icon, index) => (
                            <button
                              key={`${categoryName}-${index}-${icon}`}
                              type="button"
                              onClick={() => setFormData({ ...formData, icon })}
                              className={`p-1 text-lg rounded hover:bg-gray-100 border-2 transition-colors ${
                                formData.icon === icon ? 'border-green-500 bg-green-50' : 'border-transparent'
                              }`}
                              title={icon}
                            >
                              {icon}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <div className="grid grid-cols-5 gap-2">
              {colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`px-3 py-2 rounded-md text-sm font-medium border-2 ${
                    formData.color === color
                      ? 'border-gray-900'
                      : 'border-transparent'
                  } ${
                    color === 'blue' ? 'bg-blue-100 text-blue-800' :
                    color === 'green' ? 'bg-green-100 text-green-800' :
                    color === 'red' ? 'bg-red-100 text-red-800' :
                    color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                    color === 'purple' ? 'bg-purple-100 text-purple-800' :
                    color === 'pink' ? 'bg-pink-100 text-pink-800' :
                    color === 'indigo' ? 'bg-indigo-100 text-indigo-800' :
                    color === 'gray' ? 'bg-gray-100 text-gray-800' :
                    color === 'orange' ? 'bg-orange-100 text-orange-800' :
                    'bg-teal-100 text-teal-800'
                  }`}
                >
                  {color}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              min="0"
            />
          </div>
          </form>
        </div>

        {/* Sticky Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="category-form"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesTab;